import React from 'react';
import { CircularProgress, Divider, FormControl, Grid, MenuItem, Stack, Chip, Tooltip, InputAdornment } from '@mui/material';
import { Info as InfoIcon, Warning as WarningIcon } from '@mui/icons-material';
import styles from '../../../../../../../custom/style.module.css';
import importStyles from './consignee.module.css';
import { CustomButton, CustomInputLabel, CustomSelect, CustomTextField, FlexDiv, InputProps, SelectInputProps, StyledCard, StyledCardTitle } from '../../../../../../../custom/components';
import { AddIcon, countryCode, DeleteIcon, states_city } from 'custom/constants';
import ConsigneeHistory from './ConsigneeHistory';

const ConsigneeDetails = (props) => {
  const { action, consigneeFields, consigneeFieldsErrors, onChangeConsignee, addConsignee, editConsignee, submitting, requestingUpdate } = props;
  return (
    <StyledCard className={styles.h100}>
      <form onSubmit={action == 'add' ? addConsignee : editConsignee} onReset={props.onClose} className={`${styles.w100} ${styles.h100}`}>
        <Grid container spacing={3} className={styles.h100}>
          <Grid item xs={12} md={12}>
            <FlexDiv sx={{
              alignItems: 'baseline',
              gap: 1 
            }}>
              <StyledCardTitle mb={'6px'}>Consignee / Notify Party Details</StyledCardTitle>
              {consigneeFields?.verification_status === 'pending' && (
                <Chip 
                  label='Registration Pending' 
                  size='small' 
                  color='warning'
                  sx={{
                    height: '22px',
                    fontSize: '0.7rem',
                    mb: '6px' 
                  }}
                />
              )}
              {consigneeFields?.verification_status === 'completed' && (
                <Chip 
                  label='Registration Completed' 
                  size='small'
                  sx={{
                    height: '22px',
                    fontSize: '0.7rem',
                    mb: '6px',
                    backgroundColor: '#4caf50',
                    color: 'white'
                  }}
                />
              )}
              {action !== 'add' && (
                <ConsigneeHistory id={consigneeFields.id} />
              )}
            </FlexDiv>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='name' required>Consignee / Notify Party</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  autoFocus
                  id='name'
                  placeholder='Consignee / Notify Party'
                  type='text'
                  onChange={onChangeConsignee}
                  name='name'
                  value={consigneeFields.name || ''}
                  error={consigneeFieldsErrors.name}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='contactName'>Consignee Contact Name</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  id='contactName'
                  placeholder='Consignee Contact Name'
                  type='text'
                  onChange={onChangeConsignee}
                  name='contactName'
                  value={consigneeFields.contactName || ''}
                  error={consigneeFieldsErrors.contactName}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='email' required>Main Contact E-mail</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  id='email'
                  placeholder='E-mail'
                  type='email'
                  onChange={onChangeConsignee}
                  name='email'
                  value={consigneeFields.email || ''}
                  error={consigneeFieldsErrors.email}
                  helperText={consigneeFieldsErrors.email_msg }
                  InputProps={{
                    ...InputProps.InputProps,
                    endAdornment: consigneeFields.email_failed ? (
                      <InputAdornment position='end'>
                        <Tooltip title='Mail bounced' arrow>
                          <WarningIcon sx={{
                            color: '#d32f2f',
                            fontSize: '18px',
                            cursor: 'pointer'
                          }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='address'>Address 1</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  id='address'
                  placeholder='Address 1'
                  type='text'
                  onChange={onChangeConsignee}
                  name='address'
                  value={consigneeFields.address || ''}
                  error={consigneeFieldsErrors.address}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='address2'>Address 2</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  id='address2'
                  placeholder='Address 2'
                  type='text'
                  onChange={onChangeConsignee}
                  name='address2'
                  value={consigneeFields.address2 || ''}
                  error={consigneeFieldsErrors.address2}
                />
              </Grid>
              <Grid item xs={3} md={3}>
                <CustomInputLabel htmlFor='state'>State</CustomInputLabel>
                <FormControl variant='standard' fullWidth error={consigneeFieldsErrors.state}>
                  <CustomSelect
                    {...SelectInputProps}
                    fullWidth
                    id='state'
                    name='state'
                    value={consigneeFields.state || ''}
                    onChange={onChangeConsignee}
                  >
                    {Object.keys(states_city)?.sort(
                      (a, b) => a.localeCompare(b)
                    )?.map((state, index) =>
                      <MenuItem key={index} value={state}>{state}</MenuItem>
                    )}
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid item xs={5} md={5}>
                <CustomInputLabel htmlFor='city'>City</CustomInputLabel>
                <FormControl variant='standard' fullWidth error={consigneeFieldsErrors.city}>
                  <CustomSelect
                    {...SelectInputProps}
                    fullWidth
                    id='city'
                    name='city'
                    value={consigneeFields.city || ''}
                    onChange={onChangeConsignee}
                  >
                    {consigneeFields.state && states_city[consigneeFields.state] && states_city[consigneeFields.state].cities?.sort(
                      (a, b) => a.localeCompare(b)
                    ).map((city, index) =>
                      <MenuItem key={index} value={city}>{city}</MenuItem>
                    )}
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid item xs={4} md={4}>
                <CustomInputLabel htmlFor='zip'>Zip</CustomInputLabel>
                <CustomTextField
                  {...InputProps}
                  fullWidth
                  id='zip'
                  placeholder='Zip'
                  type='text'
                  onChange={onChangeConsignee}
                  name='zip'
                  value={consigneeFields.zip || ''}
                  error={consigneeFieldsErrors.zip}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <CustomInputLabel htmlFor='phone'>Phone</CustomInputLabel>
                <Stack direction={'row'} spacing={2}>
                  <CustomSelect
                    {...SelectInputProps}
                    id='phone_code'
                    name='phone_code'
                    value={consigneeFields.phone_code  || ''}
                    renderValue={(selected) => {
                      return selected;
                    }}
                    onChange={onChangeConsignee}
                  >
                    {countryCode?.map((code, index) =>
                      <MenuItem key={index} value={code.dial_code}>{code.name} ({code.dial_code})</MenuItem>
                    )}
                  </CustomSelect>
                  <CustomTextField
                    {...InputProps}
                    fullWidth
                    id='phone'
                    placeholder='Phone'
                    type='text'
                    onChange={onChangeConsignee}
                    name='phone'
                    value={consigneeFields.phone || ''}
                    error={consigneeFieldsErrors.phone}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider orientation='horizontal' variant='middle' flexItem />
          </Grid>
          <Grid item xs={12} md={12}>
            <FlexDiv><StyledCardTitle>Notification Email List</StyledCardTitle></FlexDiv>
            <Stack direction='row' alignItems={'center'} justifyContent='flex-end' mb={2} mt={'-24px'}>
              <AddIcon className={`${styles.addIcon} ${styles.mbauto}`} title='Add Email' onClick={props.onAddCMail} />
            </Stack>
            <Grid container spacing={2}>
              <Grid item container spacing={2} alignItems={'center'}>
                <Grid item xs={11} md={11}>
                  <CustomTextField
                    {...InputProps}
                    fullWidth
                    id='mail'
                    placeholder='E-mail'
                    value={consigneeFields.email}
                    disabled
                    InputProps={{
                      ...InputProps.InputProps,
                      endAdornment: consigneeFields.email_failed ? (
                        <InputAdornment position='end'>
                          <Tooltip title='Mail bounced' arrow>
                            <WarningIcon sx={{
                              color: '#d32f2f',
                              fontSize: '18px',
                              cursor: 'pointer'
                            }} />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={1} md={1}>
                  <DeleteIcon className={importStyles.delIcon} disabled />
                </Grid>
              </Grid>
              {consigneeFields?.mails && consigneeFields?.mails.map((mail, index) => !mail?.deleted && (
                <Grid item container spacing={2} key={index} alignItems={'center'}>
                  <Grid item xs={11} md={11}>
                    <CustomTextField
                      {...InputProps}
                      fullWidth
                      id='mail'
                      placeholder='E-mail'
                      type='email'
                      onChange={(e) => props.onChangeCMail(index, e)}
                      name='email'
                      autoFocus
                      value={mail?.email}
                      error={consigneeFieldsErrors?.mails?.[index]}
                      helperText={consigneeFieldsErrors?.mails?.[index]}
                      InputProps={{
                        ...InputProps.InputProps,
                        endAdornment: (mail?.otherConsigneeName || mail?.failed) ? (
                          <InputAdornment position='end'>
                            <Stack direction='row' spacing={0.5} alignItems='center'>
                              {mail?.failed && (
                                <Tooltip title='Mail bounced' arrow>
                                  <WarningIcon sx={{
                                    color: '#d32f2f',
                                    fontSize: '18px',
                                    cursor: 'pointer'
                                  }} />
                                </Tooltip>
                              )}
                              {mail?.otherConsigneeName && (
                                <Tooltip title={`Added for consignee: ${mail.otherConsigneeName}`} arrow>
                                  <InfoIcon sx={{
                                    color: 'var(--primaryblue)',
                                    fontSize: '18px',
                                    cursor: 'pointer'
                                  }} />
                                </Tooltip>
                              )}
                            </Stack>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={1} md={1}>
                    <DeleteIcon className={importStyles.delIcon} onClick={() => props.onDeleteCMail(index)} />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={12}>
            <Stack direction={'row'} spacing={2} alignItems={'flex-end'} justifyContent={'flex-end'} height={'100%'}>
              {(action == 'add' || action == 'edit') && (
                <CustomButton 
                  variant='outlined' 
                  onClick={(e) => props.onRequestInfoUpdate(e, props.fromModal)}
                  className={styles.mlauto}
                  disabled={!consigneeFields.name || !consigneeFields.email || requestingUpdate || submitting}
                >
                  {requestingUpdate ? <CircularProgress size={20} color='inherit' /> : 'Request Info Update'}
                </CustomButton>
              )}
              <CustomButton variant='contained' type='submit' disabled={submitting || requestingUpdate}>{submitting ? <CircularProgress size={20} color='inherit' /> : (action == 'add' ? 'Submit' : 'Update')}</CustomButton>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </StyledCard>
          
  );
};
export default ConsigneeDetails;